import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAlert } from 'react-alert'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import {
  addApplicationFavorite,
  delApplicationFavorite,
  fetchApplicationDetail,
  fetchApplicationFrequency,
} from '../../../../states/containers/analyze/analyzeSlice'
import {
  updateCurrentAnalyzeSelectDate,
  updateCurrentAnalyzeSelectMember,
  updateCurrentAnalyzeSelectTeam,
} from '../../../../states/containers/common/currentSlice'
import { fetchMemberList } from '../../../../states/containers/member/listSlice'
import { RootState } from '../../../../states/containers/store'
import { RequestApplicationDetail } from '../../../../states/types/analyze'
import { LangValue } from '../../../../states/types/common'
import { endOfMonth, format, nowDate, prevMonth, secToHour, startOfMonth, yesterDay } from '../../../../utils/date'
import { getGetPrams, setPath, setUrlParam } from '../../../../utils/url'
import BarChart from '../../../components/analyze/BarChart'
import CsvModule from '../../../components/analyze/CsvModule'
import DateModule from '../../../components/analyze/DateModule'
import SearchConditionModule from '../../../components/analyze/SearchConditionModule'
import SearchModule from '../../../components/analyze/SearchModule'
import TableList from '../../../components/analyze/TableList'
import { URL } from '../../../constants/url'
import ReactWordcloud from 'react-wordcloud'
import LoadingSquare from '../../../components/common/LoadingSquare'
import PdfModule from '../../../components/common/PdfModule'

interface GetParams {
  date: string
  team_ids: string
  member_ids: string
}

const Detail: FC = () => {
  const { name } = useParams<{ name: string }>()
  const { language } = useParams<{ language: LangValue }>()
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const { t } = useTranslation()

  const {
    analyze,
    commonPart,
    login,
    current,
  } = useSelector((state: RootState) => state)

  const [isFavorite, setIsFavorite] = useState<boolean>(false)
  const [date, setDate] = useState<string>("")
  const [teamIds, setTeamIds] = useState<number[]>([])
  const [memberIds, setMemberIds] = useState<number[]>([])

  const [isDisablePdf, setIsDisablePdf] = useState<boolean>(true)

  const endDate = useMemo(() => {
    if (Number(format(date, "YYYYMM")) === Number(nowDate("YYYYMM"))) {
      return yesterDay("YYYY-MM-DD")
    }
    return endOfMonth(date, "YYYY-MM-DD")
  }, [date])

  const clickFab = (favorite: boolean) => {
    setIsFavorite(!favorite)
    const tmpName = analyze.applicationDetail?.summary?.company_service_name
      ? `${analyze.applicationDetail?.summary?.company_service_name}(${name})`
      : name
    if (favorite) {
      dispatch(
        delApplicationFavorite(
          { service_name: name },
          () => {
            alert.success(`${tmpName}のピンを外しました。`)
          },
        ),
      )
    } else {
      dispatch(
        addApplicationFavorite(
          { service_name: name },
          () => {
            alert.success(`${tmpName}にピンを刺しました。`)
          },
        ),
      )
    }
  }

  useEffect(() => {
    const getParams = getGetPrams(location.search) as GetParams
    if (getParams.date) {
      setDate(format(getParams.date))
    } else if (current.analyzeSelectDate) {
      setDate(format(current.analyzeSelectDate))
    } else if ((Number(startOfMonth(nowDate(), "YYYYMMDD")) + 7) < Number(nowDate("YYYYMMDD"))) {
      setDate(startOfMonth(nowDate(), "YYYY-MM-DD"))
    } else {
      setDate(startOfMonth(prevMonth(nowDate()), "YYYY-MM-DD"))
    }
    if (getParams.team_ids) {
      setTeamIds(getParams.team_ids.split(',').map((value) => Number(value)))
    } else if (current.analyzeSelectTeam) {
      setTeamIds(current.analyzeSelectTeam.split(',').map((value) => Number(value)))
    }
    if (getParams.member_ids) {
      setMemberIds(getParams.member_ids.split(',').map((value) => Number(value)))
    } else if (current.analyzeSelectMember) {
      setMemberIds(current.analyzeSelectMember.split(',').map((value) => Number(value)))
    }
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    if (!date) return
    const params: RequestApplicationDetail = {
      service_name: name,
      start_date: startOfMonth(date, "YYYY-MM-DD"),
      end_date: endDate,
    }
    const query = [`date=${startOfMonth(date, "YYYY-MM-DD")}`]
    if (teamIds.length) {
      params.team_ids = teamIds
      query.push(`team_ids=${teamIds}`)
    }
    if (memberIds.length) {
      params.member_ids = memberIds
      query.push(`member_ids=${memberIds}`)
    }
    dispatch(updateCurrentAnalyzeSelectDate(date))
    dispatch(updateCurrentAnalyzeSelectTeam(teamIds.join(',')))
    dispatch(updateCurrentAnalyzeSelectMember(memberIds.join(',')))
    dispatch(fetchApplicationDetail(params))
    dispatch(fetchApplicationFrequency(params))
    history.push(`${setPath(setUrlParam(
      URL.APP_ANALYZE_DETAIL.path,
      [
        { key: 'name', value: name || "" },
      ],
    ), language)}?${query.join('&')}`)
  }, [date, teamIds, memberIds])

  useEffect(() => {
    setIsFavorite(analyze.applicationDetail?.summary?.favorite || false)
  }, [analyze.applicationDetail?.summary?.favorite])

  useEffect(() => {
    if (!login.accessToken) return
    if (!commonPart.company?.id) return
    dispatch(
      fetchMemberList(),
    )
  }, [login.accessToken, commonPart.company])

  const clickWordCloud = (keyword: string) => {
    const query = [`start_date=${startOfMonth(date, "YYYY-MM-DD")}&end_date=${endDate}`]
    if (teamIds.length) {
      query.push(`team_ids=${teamIds}`)
    }
    if (memberIds.length) {
      query.push(`member_ids=${memberIds}`)
    }
    query.push(`keywords=${keyword}&service_names=${name}`)
    history.replace(setPath(`${URL.SEARCH.path}?${query.join('&')}`, 'ja'))
  }

  const wordcloud = useMemo(() => {
    if (typeof analyze.applicationDetail?.word === 'undefined' || !analyze.applicationDetail?.word.length) return (<></>)
    const newKeywordList = analyze.applicationDetail?.word.map((value) => (
      {
        rank: value.rank,
        word: value.word,
        value: value.value,
      }
    ))
    return (
      <ReactWordcloud
        words={newKeywordList.map((value) => ({ text: value.word, value: value.value || 0 })).slice(0, 100)}
        maxWords={100}
        options={{
          rotations: 0,
          deterministic: true,
          fontSizes: [20, 100],
          enableTooltip: false,
          fontWeight: "bold",
          fontFamily: "Noto Sans CJK JP",
        }}
        callbacks={{
          onWordClick: (word) => clickWordCloud(word.text),
        }}
      />
    )
  }, [analyze.applicationDetail?.word])

  useEffect(() => {
    if (analyze.isLoading) {
      setIsDisablePdf(true)
      return
    }
    setTimeout(() => {
      setIsDisablePdf(false)
    }, 5000)
  }, [analyze])

  return (
    <section className="analyze_page">
      <Helmet>
        <title>Qasee - 組織分析 - アプリケーション分析 - 詳細</title>
      </Helmet>
      <section className="breadcrumb">
        <Link
          to={setPath(URL.ANALYZE.path, language)}
        >
          組織分析
        </Link>
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.APP_ANALYZE.path, language)}
        >
          アプリケーション分析
        </Link>
        &nbsp;&gt;&nbsp;
        {
          analyze.applicationDetail?.summary?.company_service_name
            ? `${analyze.applicationDetail?.summary?.company_service_name}(${name})`
            : name
        }
        &nbsp;
      </section>
      <section className="page_header">
        <h1 className="flex">
          {
            analyze.applicationDetail?.summary?.company_service_name
              ? `${analyze.applicationDetail?.summary?.company_service_name}(${name})`
              : name
          }
          {
            analyze.isLoading ? (
              <></>
            ) : (
              <button
                className={`favorite ${isFavorite ? "on" : "off"}`}
                type="button"
                onClick={() => clickFab(isFavorite)}
              >
                <FontAwesomeIcon icon={['fas', 'thumbtack']} />
              </button>
            )
          }
        </h1>
        <div className="tools">
          <SearchModule
            callbackTeamIds={setTeamIds}
            callbackMemberIds={setMemberIds}
          />
          <DateModule
            date={date}
            changeCallback={(callback_date) => {
              setDate(callback_date)
            }}
          />
        </div>
      </section>
      <section className="page_header">
        <div />
        <div className="tools">
          <PdfModule
            // eslint-disable-next-line max-len
            title={`Qaseeアプリケーション分析 ${analyze.applicationDetail?.summary?.company_service_name ? `${analyze.applicationDetail?.summary?.company_service_name}(${name})` : name} ${startOfMonth(date, "YYYY年MM月DD日")} ~ ${format(endDate, "YYYY年MM月DD日")}`}
            target="pdf_area"
            disabled={isDisablePdf}
          />
        </div>
      </section>
      <section id="pdf_area">
        <SearchConditionModule
          name={`アプリケーション分析：${
          analyze.applicationDetail?.summary?.company_service_name
            ? `${analyze.applicationDetail?.summary?.company_service_name}(${name})`
            : name
          }`}
          date={date}
          teamIds={teamIds}
          memberIds={memberIds}
        />
        {
          analyze.isLoading ? (
            <>
              <LoadingSquare>
                <>
                  情報を読み込んでいます…
                </>
              </LoadingSquare>
            </>
          ) : (
            <>
              {
                analyze.applicationDetail?.summary?.use_count ? (
                  <>
                    <section className="page_contents content-area">
                      <div className="content-block">
                        <div className="block-detail">
                          <div className="grid_area">
                            <div className="grid_block block_4">
                              <div className="title">
                                アクティブユーザー
                              </div>
                              <div className="value">
                                <strong>
                                  {analyze.applicationDetail?.summary.use_count}
                                </strong>
                                {`/${analyze.applicationDetail?.summary.max_use_count}人`}
                              </div>
                            </div>
                            <div className="grid_block block_4">
                              <div className="title">
                                滞在時間
                              </div>
                              <div className="value">
                                <strong>
                                  {secToHour(analyze.applicationDetail?.summary.use_time || 0).toLocaleString()}
                                </strong>
                                時間
                              </div>
                            </div>
                            <div className="grid_block block_4 reverse">
                              <div className="title">
                                利用開始からの経過日数
                              </div>
                              <div className="value">
                                <strong>
                                  {analyze.applicationDetail?.summary.use_days.toLocaleString()}
                                </strong>
                                日
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>
                    <section className="page_contents content-area">
                      <div className="content-block">
                        <div className="block-header">
                          <div className="title">
                            利用傾向
                          </div>
                        </div>
                        <div className="block-detail">
                          {
                            analyze.applicationDetail.tendency?.length ? (
                              <div className="grid_area">
                                <div className="grid_block block_6">
                                  <div className="title center">
                                    アクティブユーザー
                                  </div>
                                  <BarChart
                                    list={
                                      analyze.applicationDetail?.tendency.map((value) => (
                                        {
                                          label: format(value.label, 'YYYY年M月'),
                                          value: value.use_count,
                                        }
                                      )) || []
                                    }
                                    chartSize={200}
                                    color="#FAD58A"
                                    strongColor="#FAB019"
                                    type="person"
                                    scale
                                  />
                                </div>
                                <div className="grid_block block_6">
                                  <div className="title center">
                                    平均滞在時間
                                  </div>
                                  <BarChart
                                    list={
                                      analyze.applicationDetail?.tendency.map((value) => (
                                        {
                                          label: format(value.label, 'YYYY年M月'),
                                          value: value.use_time ? value.use_time / value.use_count : 0,
                                        }
                                      )) || []
                                    }
                                    chartSize={200}
                                    color="#B9EAEB"
                                    strongColor="#38CCCC"
                                    type="time"
                                    scale
                                  />
                                </div>
                              </div>
                            ) : (
                              <></>
                            )
                          }
                        </div>
                      </div>
                    </section>
                    <section className="page_contents content-area">
                      <div className="content-block">
                        <div className="block-header">
                          <div className="title">
                            チーム別利用状況
                          </div>
                          {
                            analyze.isFrequencyLoading || (
                              <CsvModule
                                title={`
                                  ${analyze.applicationDetail?.summary?.company_service_name
                                  ? `${analyze.applicationDetail?.summary?.company_service_name}(${name})`
                                  : name
                                  } チーム別利用状況 ${format(date, "YYYY年MM月")}
                                `}
                                labels={{
                                  name: "チーム",
                                  use_count: "利用人数",
                                  use_time: "利用時間",
                                  biz_point: "操作量",
                                  most_use_file_name: "最も利用したファイル",
                                }}
                                list={analyze.applicationDetail?.frequency?.teams || []}
                              />
                            )
                          }
                        </div>
                        <div className="block-detail">
                          {
                            analyze.isFrequencyLoading ? (
                              <>
                                <LoadingSquare>
                                  <>
                                    情報を読み込んでいます…
                                  </>
                                </LoadingSquare>
                              </>
                            ) : (
                              <>
                                <div className="grid_area">
                                  <div className="grid_block no_border">
                                    <TableList
                                      labels={{
                                        name: "チーム名",
                                        use_count: "利用人数",
                                        use_time: "利用時間",
                                        biz_point: "操作量",
                                        most_use_file_name: "最も利用したファイル",
                                      }}
                                      list={analyze.applicationDetail?.frequency?.teams || []}
                                    />
                                  </div>
                                </div>
                              </>
                            )
                          }
                        </div>
                      </div>
                    </section>
                    <section className="page_contents content-area">
                      <div className="content-block">
                        <div className="block-header">
                          <div className="title">
                            ユーザー別利用状況
                          </div>
                          {
                            analyze.isFrequencyLoading || (
                              <CsvModule
                                title={`
                                  ${analyze.applicationDetail?.summary?.company_service_name
                                  ? `${analyze.applicationDetail?.summary?.company_service_name}(${name})`
                                  : name
                                  } ユーザー別利用状況 ${format(date, "YYYY年MM月")}
                                `}
                                labels={{
                                  name: "ユーザー",
                                  use_time: "利用時間",
                                  biz_point: "操作量",
                                  most_use_file_name: "最も利用したファイル",
                                }}
                                list={analyze.applicationDetail?.frequency?.members || []}
                              />
                            )
                          }
                        </div>
                        <div className="block-detail">
                          {
                            analyze.isFrequencyLoading ? (
                              <>
                                <LoadingSquare>
                                  <>
                                    情報を読み込んでいます…
                                  </>
                                </LoadingSquare>
                              </>
                            ) : (
                              <>
                                <div className="grid_area">
                                  <div className="grid_block no_border">
                                    <TableList
                                      labels={{
                                        name: "氏名",
                                        use_time: "利用時間",
                                        biz_point: "操作量",
                                        most_use_file_name: "最も利用したファイル",
                                      }}
                                      list={analyze.applicationDetail?.frequency?.members || []}
                                    />
                                  </div>
                                </div>
                              </>
                            )
                          }
                        </div>
                      </div>
                    </section>
                    <section className="page_contents content-area">
                      <div className="content-block">
                        <div className="block-header">
                          <div className="title">
                            ファイル別利用状況
                          </div>
                          {
                            analyze.isFrequencyLoading || (
                              <CsvModule
                                title={`
                                  ${analyze.applicationDetail?.summary?.company_service_name
                                  ? `${analyze.applicationDetail?.summary?.company_service_name}(${name})`
                                  : name
                                  } ファイル別利用状況 ${format(date, "YYYY年MM月")}
                                `}
                                labels={{
                                  name: "ファイル名",
                                  use_time: "利用時間",
                                  biz_point: "操作量",
                                }}
                                list={analyze.applicationDetail?.frequency?.files || []}
                              />
                            )
                          }
                        </div>
                        <div className="block-detail">
                          {
                            analyze.isFrequencyLoading ? (
                              <>
                                <LoadingSquare>
                                  <>
                                    情報を読み込んでいます…
                                  </>
                                </LoadingSquare>
                              </>
                            ) : (
                              <>
                                <div className="grid_area">
                                  <div className="grid_block no_border">
                                    <TableList
                                      labels={{
                                        name: "ファイル名",
                                        use_time: "利用時間",
                                        biz_point: "操作量",
                                      }}
                                      list={analyze.applicationDetail?.frequency?.files || []}
                                    />
                                  </div>
                                </div>
                              </>
                            )
                          }
                        </div>
                      </div>
                    </section>
                    <section id="wordcloud" className="page_contents content-area">
                      <div className="content-block wordcloud">
                        <div className="block-header">
                          <div className="title">
                            ワードクラウド
                          </div>
                        </div>
                        <div className="block-detail">
                          {
                            analyze.applicationDetail?.word?.length ? (
                              <div className="wordcloud_image no_trans">
                                {wordcloud}
                              </div>
                            ) : (
                              <>
                                <img src="/static/images/wordcloud-sample.png" alt="" style={{ width: "100%", margin: "20px 0" }} />
                                <div className="no_image">
                                  <div className="title">
                                    {t('search.index.no_image.title', {
                                      date: `${format(date)} 〜 ${format(endDate)}`,
                                    })}
                                  </div>
                                  <div className="message">
                                    {t('search.index.no_image.message')}
                                  </div>
                                </div>
                              </>
                            )
                          }
                        </div>
                      </div>
                    </section>
                  </>
                ) : (
                  <div className="no_data_area">
                    <div className="no_data_title">
                      データがありません
                    </div>
                    <div className="no_data_text">
                      対象アプリケーションの期間条件や絞り込み条件をご確認ください。
                    </div>
                  </div>
                )
              }
            </>
          )
        }
      </section>
    </section>
  )
}

export default Detail
